import React, { useEffect, useState } from 'react';
import styles from './dsm-matrix.module.css';
import { useAppActions, useAppState } from '../../../../overmind';
import { RoleTaxonomyDto } from '../../../../models/overmind/analytics';

interface DSMMatrixData {
  roles: string[];
  skills: string[];
  relationships: Record<string, Record<string, number>>;
}

export const DSMMatrix: React.FC = () => {
  const state = useAppState() as any;
  const actions = useAppActions();
  const [loading, setLoading] = useState(true);
  const [matrixData, setMatrixData] = useState<DSMMatrixData>({ roles: [], skills: [], relationships: {} });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!state.freemiumAccount?.companySlug) {
          setError('Company slug not found');
          setLoading(false);
          return;
        }

        const rolesResponse = await actions.getRoleSkillTaxonomy({
          companySlug: state.freemiumAccount.companySlug,
          search: '',
          skillRank: 10,
          pageSize: 10,
          page: 1
        });

        if (!rolesResponse || !rolesResponse.roles) {
          setError('No roles data found');
          setLoading(false);
          return;
        }

        processRolesData(rolesResponse.roles);
      } catch (err) {
        setError('Error fetching role-skill data');
        console.error('Error fetching role-skill data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [actions, state.freemiumAccount]);

  const processRolesData = (roles: RoleTaxonomyDto[]) => {
    // Extract unique skills across all roles
    const allSkills = new Set<string>();
    roles.forEach(role => {
      role.skills.forEach(skill => {
        allSkills.add(skill.name);
      });
    });
    const skillsArray = Array.from(allSkills).sort();

    // Extract role names
    const rolesArray = roles.map(role => role.name);

    // Create relationships map
    const relationships: Record<string, Record<string, number>> = {};

    // Initialize the relationships object
    rolesArray.forEach(role => {
      relationships[role] = {};
      skillsArray.forEach(skill => {
        relationships[role][skill] = 0;
      });
    });

    // Fill in the frequency values
    roles.forEach(role => {
      role.skills.forEach(skill => {
        relationships[role.name][skill.name] = skill.frequency;
      });
    });

    setMatrixData({
      roles: rolesArray,
      skills: skillsArray,
      relationships
    });
  };

  const getCellStyle = (frequency: number) => {
    if (frequency === 0) return {};

    // Determine the size and opacity based on frequency
    const size = Math.min(24, Math.max(16, frequency * 2));
    const opacity = Math.min(1, Math.max(0.5, frequency / 10));

    // Determine color based on frequency
    let backgroundColor;
    if (frequency >= 8) {
      backgroundColor = 'var(--grape)'; // High frequency
    } else if (frequency >= 5) {
      backgroundColor = 'var(--blue)'; // Medium-high frequency
    } else if (frequency >= 3) {
      backgroundColor = 'var(--baby-blue)'; // Medium frequency
    } else {
      backgroundColor = 'var(--lilac)'; // Low frequency
    }

    return {
      width: `${size}px`,
      height: `${size}px`,
      opacity,
      backgroundColor
    };
  };

  if (loading) {
    return <div className={styles.loading}>Loading DSM matrix...</div>;
  }

  if (error) {
    return <div className={styles.error}>{error}</div>;
  }

  return (
    <div>
      <div className={styles.matrixWrapper}>
        {/* Fixed column for role names */}
        <div className={styles.fixedColumn}>
          <div className={styles.cornerHeader}></div>
          {matrixData.roles.map((role, roleIndex) => (
            <div
              key={`role-${roleIndex}`}
              className={styles.roleCell}
              title={role}
            >
              {role}
            </div>
          ))}
        </div>

        {/* Scrollable area for skills matrix */}
        <div className={styles.scrollableArea}>
          {/* Skills header row */}
          <div className={styles.skillsHeader}>
            {matrixData.skills.map((skill, index) => (
              <div
                key={`skill-${index}`}
                className={styles.skillHeaderCell}
                title={skill}
              >
                <div className={styles.verticalText}>{skill}</div>
              </div>
            ))}
          </div>

          {/* Matrix cells */}
          <div className={styles.matrixBody}>
            {matrixData.roles.map((role, roleIndex) => (
              <div key={`row-${roleIndex}`} className={styles.matrixRow}>
                {matrixData.skills.map((skill, skillIndex) => {
                  const frequency = matrixData.relationships[role][skill] || 0;
                  return (
                    <div
                      key={`cell-${roleIndex}-${skillIndex}`}
                      className={styles.matrixCell}
                      title={`${role} - ${skill}: ${frequency}`}
                    >
                      {frequency > 0 && (
                        <div
                          className={styles.relationshipDot}
                          style={getCellStyle(frequency)}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={styles.legend}>
        <div className={styles.legendItem}>
          <div className={styles.legendDot} style={{ width: '24px', height: '24px', opacity: 1, backgroundColor: 'var(--grape)' }}></div>
          <span>High Frequency (8-10)</span>
        </div>
        <div className={styles.legendItem}>
          <div className={styles.legendDot} style={{ width: '20px', height: '20px', opacity: 0.8, backgroundColor: 'var(--blue)' }}></div>
          <span>Medium-High Frequency (5-7)</span>
        </div>
        <div className={styles.legendItem}>
          <div className={styles.legendDot} style={{ width: '18px', height: '18px', opacity: 0.7, backgroundColor: 'var(--baby-blue)' }}></div>
          <span>Medium Frequency (3-4)</span>
        </div>
        <div className={styles.legendItem}>
          <div className={styles.legendDot} style={{ width: '16px', height: '16px', opacity: 0.5, backgroundColor: 'var(--lilac)' }}></div>
          <span>Low Frequency (1-2)</span>
        </div>
      </div>
    </div>
  );
};
